import React, { useEffect, useState, useMemo } from "react";
import { Card, CardBody, CardHeader, Row, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import { getDocs } from "firebase/firestore";
import { getVersionedCollection } from "../../utils/firestorePaths";
import { formatCurrency } from "../../utils/formatCurrency";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/es";

const fmt = (v) => formatCurrency(v ?? 0, true);

const StatsPreview = () => {
  const navigate = useNavigate();
  const company_id = useSelector((s) => s.companies.currentCompany?.id);
  const syncVersion = useSelector((s) => s.data.syncVersion);
  const versionDB = useSelector((s) => s.data.appConfig?.version_app || "0.0.0");

  const [dailyDocs, setDailyDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Version check (same as Stats page)
  const needsUpdate = useMemo(() => {
    if (!versionDB || versionDB === "0.0.0") return true;
    try {
      const parts = versionDB.split(".").map(Number);
      const req = [1, 2, 220];
      for (let i = 0; i < 3; i++) {
        if ((parts[i] || 0) < req[i]) return true;
        if ((parts[i] || 0) > req[i]) return false;
      }
      return false;
    } catch {
      return true;
    }
  }, [versionDB]);

  useEffect(() => {
    if (!company_id || needsUpdate) return;

    const fetchDocs = async () => {
      try {
        const statsRef = getVersionedCollection(company_id, "stats", syncVersion);
        const snapshot = await getDocs(statsRef);
        const daily = [];
        snapshot.forEach((docSnap) => {
          if (docSnap.id.startsWith("daily_")) daily.push({ id: docSnap.id, ...docSnap.data() });
        });
        daily.sort((a, b) => b.date.localeCompare(a.date));
        setDailyDocs(daily.slice(0, 14)); // Last 14 days is enough for preview
      } catch (err) {
        console.error("StatsPreview fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDocs();
  }, [company_id, syncVersion, needsUpdate]);

  // Don't render if version too old or no data
  if (needsUpdate || loading || !dailyDocs.length) return null;

  return (
    <Card className="shadow-sm mt-2 mb-3">
      <CardHeader className="bg-white py-2 d-flex align-items-center justify-content-between">
        <h6 className="mb-0 text-muted text-uppercase" style={{ fontSize: 12, letterSpacing: 0.5 }}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14 }}>bar_chart</span>
          Estadisticas recientes
          <span className="text-muted ms-1" style={{ fontSize: 11, textTransform: "none", letterSpacing: 0 }}>
            (ultimos {dailyDocs.length} dias)
          </span>
        </h6>
        <button
          className="btn btn-sm btn-soft-primary"
          onClick={() => navigate("/stats")}
        >
          <span className="material-symbols-rounded me-1" style={{ fontSize: 14, verticalAlign: "middle" }}>
            open_in_new
          </span>
          Ver estadisticas
        </button>
      </CardHeader>
      <CardBody className="pt-2 pb-3">
        <Row className="g-3">
          <Col lg={8}>
            <MiniIncomeChart docs={dailyDocs} />
          </Col>
          <Col lg={4}>
            <MiniKpis docs={dailyDocs} />
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

// ── Mini income sparkline chart ──────────────────────────────
const MiniIncomeChart = ({ docs }) => {
  const sorted = useMemo(() => [...docs].sort((a, b) => a.date.localeCompare(b.date)), [docs]);

  const options = useMemo(
    () => ({
      chart: { type: "area", height: 160, toolbar: { show: false }, fontFamily: "inherit", sparkline: { enabled: false } },
      colors: ["#34c38f", "#f46a6a"],
      stroke: { curve: "smooth", width: 2 },
      fill: { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05 } },
      dataLabels: { enabled: false },
      xaxis: {
        categories: sorted.map((d) => moment(d.date).format("DD/MM")),
        labels: { style: { fontSize: "9px" } },
      },
      yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } } },
      tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
      legend: { show: true, position: "top", fontSize: "11px" },
      grid: { borderColor: "#f1f1f1", padding: { left: 5, right: 5 } },
    }),
    [sorted]
  );

  const series = useMemo(
    () => [
      { name: "Ingresos", data: sorted.map((d) => d.income || 0) },
      { name: "Egresos", data: sorted.map((d) => d.expense || 0) },
    ],
    [sorted]
  );

  return <ReactApexChart options={options} series={series} type="area" height={160} />;
};

// ── Mini KPIs ────────────────────────────────────────────────
const MiniKpis = ({ docs }) => {
  const stats = useMemo(() => {
    const income = docs.reduce((a, d) => a + (d.income || 0), 0);
    const expense = docs.reduce((a, d) => a + (d.expense || 0), 0);
    const ticketsIn = docs.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const ticketsOut = docs.reduce((a, d) => a + (d.tickets?.total?.out || 0), 0);
    const ticketAmt = docs.reduce((a, d) => a + (d.tickets?.total?.amount || 0), 0);
    const avgTicket = ticketsOut > 0 ? ticketAmt / ticketsOut : 0;
    const dailyAvg = docs.length > 0 ? income / docs.length : 0;

    // Best day
    let bestDay = null;
    let bestIncome = 0;
    docs.forEach((d) => {
      if ((d.income || 0) > bestIncome) {
        bestIncome = d.income;
        bestDay = d.date;
      }
    });

    return { income, expense, net: income - expense, ticketsIn, dailyAvg, avgTicket, bestDay, bestIncome };
  }, [docs]);

  const items = [
    { label: "Ingresos", value: fmt(stats.income), color: "success" },
    { label: "Neto", value: fmt(stats.net), color: stats.net >= 0 ? "success" : "danger" },
    { label: "Promedio/dia", value: fmt(stats.dailyAvg), color: "primary" },
    { label: "Tickets", value: stats.ticketsIn, color: "info" },
    { label: "Ticket prom.", value: fmt(stats.avgTicket), color: "info" },
    {
      label: "Mejor dia",
      value: stats.bestDay ? moment(stats.bestDay).format("ddd DD/MM") : "-",
      color: "warning",
      sub: stats.bestIncome ? fmt(stats.bestIncome) : null,
    },
  ];

  return (
    <div className="d-flex flex-column gap-2 h-100 justify-content-center">
      {items.map((item) => (
        <div key={item.label} className="d-flex align-items-center justify-content-between" style={{ fontSize: 13 }}>
          <span className="text-muted">{item.label}</span>
          <div className="text-end">
            <span className={`fw-semibold text-${item.color}`}>{item.value}</span>
            {item.sub && <span className="text-muted ms-1" style={{ fontSize: 11 }}>({item.sub})</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StatsPreview;
